import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../services/api';
import { EMAIL_INPUT_PATTERN, EMAIL_INPUT_TITLE, isValidEmailAddress } from '../utils/emailValidation';
import { toDateInputValueInParis } from '../utils/dateTime';
import './Signup.css';

const todayParisDate = toDateInputValueInParis();

const Signup = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    date_of_birth: '',
    age: '',
    phone_number: '',
    email: '',
    student_address: '',
    student_city: '',
    guardian_phone_number: '',
    guardian_email: '',
    guardian_first_name: '',
    guardian_last_name: '',
    guardian_address: '',
    guardian_city: '',
    guardian_job: '',
    guardian_company: '',
    guardian_datebirth: '',
    country_of_birth: '',
    city_of_birth: '',
    passport_number: '',
    start_date: '',
    contract_duration: '',
    desired_city: '',
    paris_sub_region: '',
    max_budget: '',
    desired_residences: []
  });
  const [files, setFiles] = useState({
    school_certificate: null,
    user_passport: null,
    parent_passport: null,
    parent_hr_letter: null
  });
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [message, setMessage] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const navigate = useNavigate();

  const [cityCatalog, setCityCatalog] = useState(null);

  useEffect(() => {
    const loadCityCatalog = async () => {
      try {
        const response = await api.forms.getPublicCityCatalog();
        setCityCatalog(response?.data?.data || null);
      } catch (err) {
        console.error('Failed to load city catalog:', err);
      }
    };
    loadCityCatalog();
  }, []);

  const catalogCities = cityCatalog?.cities || [];
  const selectedCityObj = catalogCities.find((c) => c.name === formData.desired_city);
  const cityHasSubRegions = Array.isArray(selectedCityObj?.sub_regions) && selectedCityObj.sub_regions.length > 0;
  const subRegions = cityHasSubRegions ? selectedCityObj.sub_regions : [];
  const availableResidences = cityHasSubRegions
    ? (subRegions.find((sr) => sr.name === formData.paris_sub_region)?.residences || [])
    : (selectedCityObj?.residences || []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const newData = { ...prev, [name]: value };
      
      if (name === 'desired_city') {
        newData.paris_sub_region = '';
        newData.desired_residences = [];
      }
      
      if (name === 'paris_sub_region') {
        newData.desired_residences = [];
      }
      
      return newData;
    });
  };

  const handleCheckboxChange = (residence) => {
    setFormData(prev => {
      const current = prev.desired_residences;
      if (current.includes(residence)) {
        return { ...prev, desired_residences: current.filter(r => r !== residence) };
      } else if (current.length < 3) {
        return { ...prev, desired_residences: [...current, residence] };
      } else {
        return prev;
      }
    });
  };

  const handleFileChange = (e) => {
    const { name, files: fileList } = e.target;
    const file = fileList[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        setMessage('Seuls les fichiers PDF sont acceptés.');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        setMessage('La taille du fichier doit être inférieure à 10 Mo.');
        return;
      }
    }
    setFiles(prev => ({ ...prev, [name]: file }));
    setMessage('');
  };

  const validateStep = (step) => {
    setMessage('');
    if (step === 1) {
      if (!isValidEmailAddress(formData.email) || !isValidEmailAddress(formData.guardian_email)) {
        setMessage('Veuillez saisir une adresse email valide contenant @ pour l\'étudiant et le garant.');
        return false;
      }
      if (!formData.first_name || !formData.last_name || !formData.date_of_birth || 
          !formData.phone_number || !formData.email ||
          !formData.country_of_birth || !formData.city_of_birth || !formData.passport_number ||
          !formData.student_address || !formData.student_city ||
          !formData.guardian_phone_number ||
          !formData.guardian_email ||
          !formData.guardian_first_name || !formData.guardian_last_name ||
          !formData.guardian_address || !formData.guardian_city ||
          !formData.guardian_job || !formData.guardian_company || !formData.guardian_datebirth) {
        setMessage('Veuillez remplir tous les champs obligatoires.');
        return false;
      }
    } else if (step === 2) {
      if (!formData.start_date || !formData.contract_duration || !formData.desired_city ||
          !formData.max_budget) {
        setMessage('Veuillez remplir tous les champs obligatoires.');
        return false;
      }
      if (formData.start_date < todayParisDate) {
        setMessage('La date de début ne peut pas être dans le passé.');
        return false;
      }
      if (formData.desired_city === 'Paris' && !formData.paris_sub_region) {
        setMessage('Veuillez sélectionner une sous-région de Paris.');
        return false;
      }
      if (formData.desired_residences.length === 0) {
        setMessage('Veuillez sélectionner au moins une résidence.');
        return false;
      }
    } else if (step === 3) {
      for (const key of Object.keys(files)) {
        if (!files[key]) {
          setMessage('Veuillez télécharger tous les documents requis.');
          return false;
        }
      }
      if (!termsAccepted) {
        setMessage('Veuillez accepter les conditions générales.');
        return false;
      }
    }
    return true;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => prev - 1);
    setMessage('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep(3)) return;

    const formDataToSend = new FormData();
    Object.keys(formData).forEach(key => {
      if (key === 'desired_residences') {
        formDataToSend.append(key, JSON.stringify(formData[key]));
      } else {
        formDataToSend.append(key, formData[key]);
      }
    });
    Object.keys(files).forEach(key => {
      if (files[key]) {
        formDataToSend.append(key, files[key]);
      }
    });

    try {
      await api.signup.createRequest(formDataToSend);
      setShowSuccessModal(true);
    } catch (error) {
      console.log('Submission error:', error);
      const errorMessage = error.response?.data?.error || 'Échec de la soumission. Veuillez réessayer.';
      setMessage(errorMessage);
    }
  };

  const displayCities = catalogCities.map((c) => c.name);

  return (
    <div className="signup-page request-form-page">
      {/* Hero Banner */}
      <div className="signup-hero">
        <div className="hero-content">
          <h1>Demander un logement</h1>
          <p>Remplissez le formulaire pour faire votre demande de logement étudiant</p>
        </div>
      </div>

      <div className="signup-container">
        {/* Progress Steps */}
        <div className="progress-bar request-form-progress">
          <div className={`progress-step ${currentStep >= 1 ? 'active' : ''} ${currentStep > 1 ? 'completed' : ''}`}>
            <div className="step-circle">1</div>
            <span>Informations</span>
          </div>
          <div className="progress-line"></div>
          <div className={`progress-step ${currentStep >= 2 ? 'active' : ''} ${currentStep > 2 ? 'completed' : ''}`}>
            <div className="step-circle">2</div>
            <span>Logement</span>
          </div>
          <div className="progress-line"></div>
          <div className={`progress-step ${currentStep >= 3 ? 'active' : ''}`}>
            <div className="step-circle">3</div>
            <span>Documents</span>
          </div>
        </div>

        {/* Form Card */}
        <div className="signup-card">
          {message && <div className="message error">{message}</div>}

          <form onSubmit={handleSubmit} encType="multipart/form-data" className="adeef-uiform">
            {/* Step 1: Personal Information */}
            {currentStep === 1 && (
              <div className="form-step">
                <h2 className="step-title">Informations personnelles</h2>
                <section className="signup-section">
                  <div className="form-grid">
                  <div className="form-group">
                    <label className="adeef-uiform-field">
                      <input className="adeef-uiform-input" type="text" name="first_name" value={formData.first_name} onChange={handleChange} placeholder=" " />
                      <span>Prénom <span className="required">*</span></span>
                    </label>
                  </div>
                  <div className="form-group">
                    <label className="adeef-uiform-field">
                      <input className="adeef-uiform-input" type="text" name="last_name" value={formData.last_name} onChange={handleChange} placeholder=" " />
                      <span>Nom <span className="required">*</span></span>
                    </label>
                  </div>
                  <div className="form-group">
                    <label className="adeef-uiform-field" data-filled={formData.date_of_birth ? 'true' : 'false'}>
                      <input className="adeef-uiform-input" type="date" name="date_of_birth" value={formData.date_of_birth} onChange={handleChange} max={todayParisDate} />
                      <span>Date de naissance <span className="required">*</span></span>
                    </label>
                  </div>
                  <div className="form-group">
                    <label className="adeef-uiform-field">
                      <input className="adeef-uiform-input" type="number" name="age" value={formData.age} onChange={handleChange} placeholder=" " />
                      <span>Age</span>
                    </label>
                  </div>
                  <div className="form-group">
                    <label className="adeef-uiform-field">
                      <input className="adeef-uiform-input" type="text" name="phone_number" value={formData.phone_number} onChange={handleChange} placeholder=" " />
                      <span>Téléphone <span className="required">*</span></span>
                    </label>
                  </div>
                  <div className="form-group">
                    <label className="adeef-uiform-field">
                      <input className="adeef-uiform-input" type="email" name="email" value={formData.email} onChange={handleChange} placeholder=" " pattern={EMAIL_INPUT_PATTERN} title={EMAIL_INPUT_TITLE} />
                      <span>Email <span className="required">*</span></span>
                    </label>
                  </div>

                  <div className="form-group">
                    <label className="adeef-uiform-field">
                      <input className="adeef-uiform-input" type="text" name="country_of_birth" value={formData.country_of_birth} onChange={handleChange} placeholder=" " />
                      <span>Pays de naissance <span className="required">*</span></span>
                    </label>
                  </div>
                  <div className="form-group">
                    <label className="adeef-uiform-field">
                      <input className="adeef-uiform-input" type="text" name="city_of_birth" value={formData.city_of_birth} onChange={handleChange} placeholder=" " />
                      <span>Ville de naissance <span className="required">*</span></span>
                    </label>
                  </div>
                  <div className="form-group full-width">
                    <label className="adeef-uiform-field">
                      <input className="adeef-uiform-input" type="text" name="passport_number" value={formData.passport_number} onChange={handleChange} placeholder=" " />
                      <span>Numéro de passeport <span className="required">*</span></span>
                    </label>
                  </div>
                </div>
                </section>

                <section className="signup-section">
                  <h3 className="step-title step-title--section signup-section-title">Adresse de l'étudiant</h3>
                  <div className="form-grid">
                    <div className="form-group">
                      <label className="adeef-uiform-field">
                        <input className="adeef-uiform-input" type="text" name="student_address" value={formData.student_address} onChange={handleChange} placeholder=" " />
                        <span>Adresse <span className="required">*</span></span>
                      </label>
                    </div>
                    <div className="form-group">
                      <label className="adeef-uiform-field">
                        <input className="adeef-uiform-input" type="text" name="student_city" value={formData.student_city} onChange={handleChange} placeholder=" " />
                        <span>Ville <span className="required">*</span></span>
                      </label>
                    </div>
                  </div>
                </section>

                <section className="signup-section">
                  <h3 className="step-title step-title--section signup-section-title guardian-heading">
                    <span className="guardian-heading-label">Informations du garant</span>
                    <span className="guardian-info">(<span className="guardian-text">Un garant est une personne qui promet de payer le loyer d'un locataire si celui-ci ne peut pas le payer. il peut être votre parent en Égypte</span>)</span>
                  </h3>
                  <div className="form-grid">
                  <div className="form-group">
                    <label className="adeef-uiform-field">
                      <input className="adeef-uiform-input" type="text" name="guardian_first_name" value={formData.guardian_first_name} onChange={handleChange} placeholder=" " />
                      <span>Prénom (garant) <span className="required">*</span></span>
                    </label>
                  </div>
                  <div className="form-group">
                    <label className="adeef-uiform-field">
                      <input className="adeef-uiform-input" type="text" name="guardian_last_name" value={formData.guardian_last_name} onChange={handleChange} placeholder=" " />
                      <span>Nom (garant) <span className="required">*</span></span>
                    </label>
                  </div>
                  <div className="form-group">
                    <label className="adeef-uiform-field">
                      <input className="adeef-uiform-input" type="text" name="guardian_phone_number" value={formData.guardian_phone_number} onChange={handleChange} placeholder=" " />
                      <span>Téléphone (garant) <span className="required">*</span></span>
                    </label>
                  </div>
                  <div className="form-group">
                    <label className="adeef-uiform-field">
                      <input className="adeef-uiform-input" type="email" name="guardian_email" value={formData.guardian_email} onChange={handleChange} placeholder=" " pattern={EMAIL_INPUT_PATTERN} title={EMAIL_INPUT_TITLE} />
                      <span>Email (garant) <span className="required">*</span></span>
                    </label>
                  </div>
                  <div className="form-group">
                    <label className="adeef-uiform-field">
                      <input className="adeef-uiform-input" type="text" name="guardian_address" value={formData.guardian_address} onChange={handleChange} placeholder=" " />
                      <span>Adresse (garant) <span className="required">*</span></span>
                    </label>
                  </div>
                  <div className="form-group">
                    <label className="adeef-uiform-field">
                      <input className="adeef-uiform-input" type="text" name="guardian_city" value={formData.guardian_city} onChange={handleChange} placeholder=" " />
                      <span>Ville (garant) <span className="required">*</span></span>
                    </label>
                  </div>
                  <div className="form-group">
                    <label className="adeef-uiform-field">
                      <input className="adeef-uiform-input" type="text" name="guardian_job" value={formData.guardian_job} onChange={handleChange} placeholder=" " />
                      <span>Profession (garant) <span className="required">*</span></span>
                    </label>
                  </div>
                  <div className="form-group">
                    <label className="adeef-uiform-field">
                      <input className="adeef-uiform-input" type="text" name="guardian_company" value={formData.guardian_company} onChange={handleChange} placeholder=" " />
                      <span>Société (garant) <span className="required">*</span></span>
                    </label>
                  </div>
                  <div className="form-group full-width">
                    <label className="adeef-uiform-field" data-filled={formData.guardian_datebirth ? 'true' : 'false'}>
                      <input className="adeef-uiform-input" type="date" name="guardian_datebirth" value={formData.guardian_datebirth} onChange={handleChange} max={todayParisDate} />
                      <span>Date de naissance (garant) <span className="required">*</span></span>
                    </label>
                  </div>
                </div>
                </section>
              </div>
            )}

            {/* Step 2: Housing Preferences */}
            {currentStep === 2 && (
              <div className="form-step">
                <h2 className="step-title">Préférences de logement</h2>
                <section className="signup-section">
                  <div className="form-grid three-cols">
                  <div className="form-group">
                    <label className="adeef-uiform-field" data-filled={formData.start_date ? 'true' : 'false'}>
                      <input className="adeef-uiform-input" type="date" name="start_date" value={formData.start_date} min={todayParisDate} onChange={handleChange} />
                      <span>Date de début <span className="required">*</span></span>
                    </label>
                  </div>
                  <div className="form-group">
                    <label className="adeef-uiform-field" data-filled={formData.contract_duration ? 'true' : 'false'}>
                      <select className="adeef-uiform-input" name="contract_duration" value={formData.contract_duration} onChange={handleChange}>
                        <option value="">Sélectionner</option>
                        <option value="2-3">2-3 mois</option>
                        <option value="3-6">3-6 mois</option>
                        <option value="6-9">6-9 mois</option>
                        <option value="9-12">9-12 mois</option>
                      </select>
                      <span>Durée du contrat <span className="required">*</span></span>
                    </label>
                  </div>
                  <div className="form-group">
                    <label className="adeef-uiform-field">
                      <input className="adeef-uiform-input" type="number" name="max_budget" value={formData.max_budget} onChange={handleChange} placeholder=" " />
                      <span>Budget max (€) <span className="required">*</span></span>
                    </label>
                  </div>
                  <div className="form-group">
                    <label className="adeef-uiform-field" data-filled={formData.desired_city ? 'true' : 'false'}>
                      <select className="adeef-uiform-input" name="desired_city" value={formData.desired_city} onChange={handleChange}>
                        <option value="">Sélectionner une ville</option>
                        {displayCities.map(city => <option key={city} value={city}>{city}</option>)}
                      </select>
                      <span>Ville souhaitée <span className="required">*</span></span>
                    </label>
                  </div>
                  {cityHasSubRegions && (
                    <div className="form-group">
                      <label className="adeef-uiform-field" data-filled={formData.paris_sub_region ? 'true' : 'false'}>
                        <select className="adeef-uiform-input" name="paris_sub_region" value={formData.paris_sub_region} onChange={handleChange}>
                          <option value="">Sélectionner</option>
                          {subRegions.map(subRegion => <option key={subRegion.name} value={subRegion.name}>{subRegion.name}</option>)}
                        </select>
                        <span>Sous-région <span className="required">*</span></span>
                      </label>
                    </div>
                  )}
                </div>

                {availableResidences.length > 0 && (
                  <div className="residences-section">
                    <label>Résidences souhaitées <span className="required">*</span> <span className="hint">(max 3)</span></label>
                    <div className="residences-grid">
                      {availableResidences.map(residence => (
                        <label key={residence} className={`residence-option ${formData.desired_residences.includes(residence) ? 'selected' : ''}`}>
                          <input
                            type="checkbox"
                            checked={formData.desired_residences.includes(residence)}
                            onChange={() => handleCheckboxChange(residence)}
                          />
                          <span>{residence}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                </section>
              </div>
            )}

            {/* Step 3: Documents */}
            {currentStep === 3 && (
              <div className="form-step">
                <h2 className="step-title">Documents requis</h2>
                <p className="step-subtitle">Tous les documents doivent être au format PDF (max 10 Mo)</p>
                
                <section className="signup-section">
                  <div className="documents-grid">
                  <div className="document-upload">
                    <label>
                      <span className="doc-icon">📄</span>
                      <span className="doc-name">Certificat de scolarité</span>
                      <input type="file" name="school_certificate" accept="application/pdf" onChange={handleFileChange} />
                      <span className={`doc-status ${files.school_certificate ? 'uploaded' : ''}`}>
                        {files.school_certificate ? '✓ ' + files.school_certificate.name : 'Cliquez pour télécharger'}
                      </span>
                    </label>
                  </div>
                  <div className="document-upload">
                    <label>
                      <span className="doc-icon">🛂</span>
                      <span className="doc-name">Passeport étudiant</span>
                      <input type="file" name="user_passport" accept="application/pdf" onChange={handleFileChange} />
                      <span className={`doc-status ${files.user_passport ? 'uploaded' : ''}`}>
                        {files.user_passport ? '✓ ' + files.user_passport.name : 'Cliquez pour télécharger'}
                      </span>
                    </label>
                  </div>
                  <div className="document-upload">
                    <label>
                      <span className="doc-icon">🛂</span>
                      <span className="doc-name">Passeport parent</span>
                      <input type="file" name="parent_passport" accept="application/pdf" onChange={handleFileChange} />
                      <span className={`doc-status ${files.parent_passport ? 'uploaded' : ''}`}>
                        {files.parent_passport ? '✓ ' + files.parent_passport.name : 'Cliquez pour télécharger'}
                      </span>
                    </label>
                  </div>
                  <div className="document-upload">
                    <label>
                      <span className="doc-icon">📋</span>
                      <span className="doc-name">Lettre employeur</span>
                      <input type="file" name="parent_hr_letter" accept="application/pdf" onChange={handleFileChange} />
                      <span className={`doc-status ${files.parent_hr_letter ? 'uploaded' : ''}`}>
                        {files.parent_hr_letter ? '✓ ' + files.parent_hr_letter.name : 'Cliquez pour télécharger'}
                      </span>
                    </label>
                  </div>
                </div>

                <div className="terms-section">
                  <label className="terms-checkbox">
                    <input
                      type="checkbox"
                      checked={termsAccepted}
                      onChange={(e) => setTermsAccepted(e.target.checked)}
                    />
                    <span>Je déclare avoir lu et accepter sans réserve les <Link to="/cgu">conditions générales d'utilisation</Link> et la <Link to="/privacy">politique de confidentialité</Link></span>
                  </label>
                </div>
                </section>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="form-navigation">
              {currentStep > 1 && (
                <button type="button" className="btn-prev" onClick={prevStep}>
                  ← Précédent
                </button>
              )}
              {currentStep < 3 ? (
                <button type="button" className="btn-next" onClick={nextStep}>
                  Suivant →
                </button>
              ) : (
                <button type="submit" className="btn-submit">
                  Soumettre la demande
                </button>
              )}
            </div>
          </form>
        </div>

        {/* Back Link */}
        <div className="back-link">
          <Link to="/">&larr; Retour à l'accueil</Link>
        </div>
      </div>

      {/* Success State */}
      {showSuccessModal && (
        <div className="signup-card">
          <div className="request-form-success">
            <h3>Demande envoyée avec succès</h3>
            <p>Votre demande a été enregistrée. Consultez votre email pour le code de suivi de votre dossier.</p>
            <div className="form-navigation">
              <button type="button" className="btn-submit" onClick={() => navigate('/')}>
                Retour à l'accueil
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Signup;
